import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Card, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Auth } from '@aws-amplify/auth';
import CircularLoader from '../loader/CircularLoader';
import FullscreenLoader from '../loader/FullscreenLoader';
import CenterlineIcon from '../../images/CenterlineAddInLogo.png';
import {
  getMyExternalProviders,
  linkGoogleExternalProvider,
  unlinkGoogleExternalProvider,
} from '../../models/api/external-provider';
import { useDispatch, useSelector } from 'react-redux';
import { getUserState } from '../../features/user/selectors';
import { fetchUser, setUser } from '../../features/user/actions';
import { resetApiClient } from '../../api-client/api-client';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: 300,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    width: 200,
    marginBottom: 24,
  },
  field: {
    width: '100%',
    marginBottom: 16,
  },
  button: {
    width: '100%',
  },
  statusMessage: {
    width: '100%',
    marginTop: 8,
  },
  helperText: {
    width: '100%',
    marginBottom: 16,
    textAlign: 'center',
  },
  buttonRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 16,
  },
  unlinkButton: {
    backgroundColor: '#ED3F26',
    '&:hover': {
      backgroundColor: '#FF5D45',
    },
  },
  signOutButton: {
    marginTop: 32,
  },
}));

function GmailAuthLink() {
  const classes = useStyles();

  const dispatch = useDispatch();
  const user = useSelector(getUserState);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [statusMessage, setStatusMessage] = useState<{
    text: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  } | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [linkedEmail, setLinkedEmail] = useState<string | null>(null);
  const [googleIdToken, setGoogleIdToken] = useState<string | null>(() => {
    const idTokenFromUrl = new URLSearchParams(window.location.search).get('idToken');
    return idTokenFromUrl || sessionStorage.getItem('gmailLinkIdToken');
  });

  useEffect(() => {
    const idTokenFromUrl = new URLSearchParams(window.location.search).get('idToken');
    if (idTokenFromUrl) {
      sessionStorage.setItem('gmailLinkIdToken', idTokenFromUrl);
      setGoogleIdToken(idTokenFromUrl);
    }
  }, []);

  const loadLinkedEmail = useCallback(async () => {
    const response = await getMyExternalProviders();
    const googleLink = (response ?? []).find(
      (link: { provider?: string; accountEmail?: string | null }) => link.provider === 'google',
    );
    setLinkedEmail(googleLink?.accountEmail ?? null);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        await Auth.currentSession();
        if (!isMounted) return;

        setHasSession(true);

        if (!user.id) {
          await dispatch(fetchUser());
          if (!isMounted) return;
        }

        await loadLinkedEmail();
      } catch (error) {
        if (!isMounted) return;
        setHasSession(false);
        setLinkedEmail(null);
      } finally {
        if (isMounted) setIsInitializing(false);
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [dispatch, loadLinkedEmail, user.id]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setStatusMessage(null);

    if (!email.trim() || !password.trim()) {
      setStatusMessage({ text: 'Email and password are required', severity: 'error' });
      setIsLoggingIn(false);
      return;
    }

    try {
      await Auth.signIn(email.trim(), password.trim());
      resetApiClient();
      await dispatch(fetchUser());
      await loadLinkedEmail();
      setHasSession(true);
      setStatusMessage({ text: 'Logged in successfully', severity: 'success' });
    } catch (error: any) {
      console.error('Sign-in error:', error);
      setStatusMessage({ text: error?.message || 'Failed to log in', severity: 'error' });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLink = useCallback(async () => {
    if (!googleIdToken) {
      setStatusMessage({
        text:
          'No Gmail link token was provided. Open this page from the Gmail add-on to link a Gmail account.',
        severity: 'warning',
      });
      return;
    }

    setIsLinking(true);
    setStatusMessage(null);

    try {
      const response = await linkGoogleExternalProvider({ idToken: googleIdToken });
      setLinkedEmail(response.accountEmail ?? null);
      sessionStorage.removeItem('gmailLinkIdToken');
      window.history.replaceState({}, '', window.location.pathname);

      setStatusMessage({ text: 'Gmail account linked successfully!', severity: 'success' });
    } catch (error: any) {
      console.error('Failed to link Gmail account:', error);
      setStatusMessage({
        text:
          error?.response?.data?.userFriendlyMessage ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to link Gmail account',
        severity: 'error',
      });
    } finally {
      setIsLinking(false);
    }
  }, [googleIdToken]);

  const handleUnlink = useCallback(async () => {
    setIsUnlinking(true);
    setStatusMessage(null);

    try {
      await unlinkGoogleExternalProvider();
      setLinkedEmail(null);
      setStatusMessage({ text: 'Gmail account unlinked successfully.', severity: 'success' });
    } catch (error: any) {
      console.error('Failed to unlink Gmail account:', error);
      setStatusMessage({
        text:
          error?.response?.data?.userFriendlyMessage ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to unlink Gmail account',
        severity: 'error',
      });
    } finally {
      setIsUnlinking(false);
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    await Auth.signOut();
    dispatch(setUser());
    setHasSession(false);
    setLinkedEmail(null);
    setStatusMessage({ text: 'Signed out of CENTERLINE.', severity: 'info' });
  }, [dispatch]);

  useEffect(() => {
    if (hasSession && googleIdToken) {
      handleLink();
    }
  }, [googleIdToken, handleLink, hasSession]);

  if (isInitializing || (hasSession && !!googleIdToken && isLinking && !linkedEmail)) {
    return <FullscreenLoader />;
  }

  return (
    <div className={classes.root}>
      <Card className={classes.card} elevation={3}>
        <img src={CenterlineIcon} alt="CENTERLINE logo" className={classes.logo} />
        <Typography variant="h6" className={classes.helperText}>
          Gmail Account Linking
        </Typography>

        {isLoggingIn || isLinking || isUnlinking ? (
          <Box width="100%" textAlign="center">
            <CircularLoader />
            <Typography style={{ marginTop: 8, fontSize: 14 }}>
              {isLoggingIn
                ? 'Signing in...'
                : isUnlinking
                ? 'Removing your Gmail link...'
                : 'Linking your Gmail account...'}
            </Typography>
          </Box>
        ) : (
          <>
            {!hasSession && (
              <Typography variant="body2" className={classes.helperText}>
                Sign in to CENTERLINE to link or manage the Gmail account used by the add-on.
              </Typography>
            )}

            {!hasSession && (
              <form onSubmit={handleSignIn} style={{ width: '100%' }}>
                <TextField
                  className={classes.field}
                  label="Email"
                  variant="outlined"
                  size="small"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                />
                <TextField
                  className={classes.field}
                  label="Password"
                  type="password"
                  variant="outlined"
                  size="small"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!email || !password}
                >
                  Sign In
                </Button>
              </form>
            )}

            {statusMessage && (
              <Alert severity={statusMessage.severity} className={classes.statusMessage}>
                {statusMessage.text}
              </Alert>
            )}

            {hasSession && linkedEmail && (
              <Alert severity="info" className={classes.statusMessage}>
                Linked Gmail account: {linkedEmail}
              </Alert>
            )}

            {hasSession && !linkedEmail && !googleIdToken && (
              <Alert severity="info" className={classes.statusMessage}>
                No Gmail account is currently linked. Open this page from the Gmail add-on to link
                the active Gmail account.
              </Alert>
            )}

            {hasSession && (
              <div className={classes.buttonRow}>
                <Button
                  className={classes.button}
                  variant="contained"
                  color="primary"
                  onClick={handleLink}
                  disabled={!googleIdToken}
                >
                  {linkedEmail ? 'Relink Gmail Account' : 'Link Gmail Account'}
                </Button>
                {linkedEmail && (
                  <Button
                    className={`${classes.button} ${classes.unlinkButton}`}
                    variant="contained"
                    color="secondary"
                    onClick={handleUnlink}
                  >
                    Unlink Gmail Account
                  </Button>
                )}
                <Button
                  className={`${classes.button} ${classes.signOutButton}`}
                  color="primary"
                  variant="outlined"
                  onClick={handleSignOut}
                >
                  Sign Out
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default GmailAuthLink;
